"use client";

// Prestandavakten RUM: what a student actually waits for, measured in a sample of tab
// sessions (share set on /admin/prestanda; "0" = off). Anonymous — no id is sent, and
// every path is reduced to a template (also again on the server). Mounted for students
// only (RootLayout), which hands it the share. Renders nothing.
//
// Off (share 0) it touches nothing on the device: no storage, no measurement, and the
// polls send no measurement flags (lib/klient-matning.ts). It used to fetch the share and
// store "0" in sessionStorage in every student tab — storage the integritetspolicy (§14)
// does not list (avtalsgranskning 2026-10-26).
//
// Cost when on: one sendBeacon per minute with data, and on tab hide. PerformanceObserver
// is passive: nothing wraps fetch, so the measurement cannot slow the requests it measures.

import { useEffect } from "react";
import { sattKlientMatning } from "@/lib/klient-matning";
import { sokvagsMall } from "@/lib/prestanda/mall";

const NYCKEL = "vektr-prestanda-rum";
const URL_RUM = "/api/prestanda/rum";
const INTERVALL_MS = 60_000;
const MAX_POSTER = 60;

type Post = { mall: string; ms: number };

export default function PrestandaRum({ andel }: { andel: number }) {
  useEffect(() => {
    sattKlientMatning(andel > 0);
    if (!(andel > 0)) return;
    let avslutad = false;
    const stada: (() => void)[] = [];

    function bestam(): boolean {
      try {
        const sparad = sessionStorage.getItem(NYCKEL);
        if (sparad === "1" || sparad === "0") return sparad === "1";
      } catch {
        // storage blocked: decide once for this page view
      }
      const pa = Math.random() < andel;
      try {
        sessionStorage.setItem(NYCKEL, pa ? "1" : "0");
      } catch {
        // ignore
      }
      return pa;
    }

    function starta() {
      let ny = true;
      const sidor: { mall: string; ttfb: number; last: number }[] = [];
      const mjuka: Post[] = [];
      const api: Post[] = [];

      const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
      if (nav) {
        const fanga = () =>
          sidor.push({
            mall: sokvagsMall(location.pathname),
            ttfb: Math.round(nav.responseStart),
            last: Math.round(nav.loadEventEnd || nav.domContentLoadedEventEnd || nav.responseEnd),
          });
        if (document.readyState === "complete") fanga();
        else {
          const vid = () => setTimeout(fanga, 0);
          addEventListener("load", vid, { once: true });
          stada.push(() => removeEventListener("load", vid));
        }
      }

      let obs: PerformanceObserver | null = null;
      try {
        obs = new PerformanceObserver((lista) => {
          for (const e of lista.getEntries() as PerformanceResourceTiming[]) {
            if (e.initiatorType !== "fetch" && e.initiatorType !== "xmlhttprequest") continue;
            let u: URL;
            try {
              u = new URL(e.name);
            } catch {
              continue;
            }
            if (u.origin !== location.origin || u.pathname === URL_RUM) continue;
            const post = { mall: sokvagsMall(u.pathname), ms: Math.round(e.duration) };
            // Next's client navigations fetch the page as RSC: a "soft" page load.
            if (u.searchParams.has("_rsc")) {
              if (mjuka.length < MAX_POSTER) mjuka.push(post);
            } else if (u.pathname.startsWith("/api/")) {
              if (api.length < MAX_POSTER) api.push(post);
            }
          }
        });
        obs.observe({ type: "resource", buffered: false });
      } catch {
        obs = null;
      }

      const enhet = matchMedia("(max-width: 767px)").matches ? "mobil" : "dator";
      const natt = (navigator as Navigator & { connection?: { effectiveType?: string } }).connection?.effectiveType ?? "okand";

      function skicka() {
        if (!ny && sidor.length + mjuka.length + api.length === 0) return;
        const kropp = JSON.stringify({ ny, enhet, natt, sidor: sidor.splice(0), mjuka: mjuka.splice(0), api: api.splice(0) });
        ny = false;
        try {
          navigator.sendBeacon(URL_RUM, new Blob([kropp], { type: "text/plain" }));
        } catch {
          // ignore
        }
      }

      const tid = setInterval(skicka, INTERVALL_MS);
      const vidDold = () => {
        if (document.visibilityState === "hidden") skicka();
      };
      document.addEventListener("visibilitychange", vidDold);
      stada.push(() => {
        clearInterval(tid);
        document.removeEventListener("visibilitychange", vidDold);
        obs?.disconnect();
        skicka();
      });
    }

    // After the page has settled: the measurement never competes with the first load.
    const vanta = setTimeout(() => {
      if (bestam() && !avslutad) starta();
    }, 3_000);

    return () => {
      avslutad = true;
      clearTimeout(vanta);
      for (const f of stada) f();
    };
  }, [andel]);

  return null;
}
